<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
  selectedLabel: { type: String, default: '' },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
  'selectLabel',
  'downloadTemplate',
  'basicImport',
  'basicExport',
]);

const { t } = useI18n();
const labels = useMapGetter('labels/getLabels');

const showLabelDropdown = ref(false);
const labelQuery = ref('');

const filteredLabels = computed(() => {
  if (!labelQuery.value) return labels.value;
  return labels.value.filter(label =>
    label.title.toLowerCase().includes(labelQuery.value.toLowerCase())
  );
});

const toggleLabelDropdown = () => {
  showLabelDropdown.value = !showLabelDropdown.value;
  labelQuery.value = '';
};

const closeLabelDropdown = () => {
  showLabelDropdown.value = false;
};

const selectLabel = labelTitle => {
  emit('selectLabel', labelTitle);
  closeLabelDropdown();
};

const clearLabelFilter = () => {
  emit('selectLabel', '');
  closeLabelDropdown();
};
</script>

<template>
  <header class="sticky top-0 z-20 px-6">
    <div
      class="flex items-start sm:items-center justify-between w-full py-6 gap-2 mx-auto max-w-5xl"
    >
      <span class="text-xl font-medium truncate text-n-slate-12">
        {{ headerTitle }}
      </span>
      <div class="flex items-center flex-col sm:flex-row flex-shrink-0 gap-4">
        <div v-if="showSearch" class="flex items-center gap-2 w-full">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            class="w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
        <div class="flex items-center flex-shrink-0 gap-4">
          <div class="flex items-center gap-2">
            <div v-if="!isLabelView && !isActiveView" class="relative">
              <Button
                id="toggleContactsFilterButton"
                :icon="
                  isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
                "
                color="slate"
                size="sm"
                class="relative w-8"
                variant="ghost"
                @click="emit('filter')"
              >
                <div
                  v-if="hasActiveFilters && !isSegmentsView"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>
              <slot name="filter" />
            </div>
            <div v-if="!isLabelView && !isActiveView" class="relative">
              <Button
                icon="i-lucide-tag"
                color="slate"
                size="sm"
                class="relative w-8"
                variant="ghost"
                @click="toggleLabelDropdown"
              >
                <div
                  v-if="selectedLabel"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>
              <div
                v-if="showLabelDropdown"
                v-on-click-outside="closeLabelDropdown"
                class="absolute right-0 top-10 z-20 w-64 bg-n-alpha-3 backdrop-blur-[100px] border-n-weak rounded-lg border border-solid shadow-md"
                role="dialog"
              >
                <div
                  class="flex items-center justify-between p-2.5 border-b border-n-weak"
                >
                  <span class="text-sm font-medium">{{
                    t('CONTACTS_LAYOUT.HEADER.FILTER_BY_LABEL')
                  }}</span>
                  <Button
                    icon="i-lucide-x"
                    variant="ghost"
                    size="xs"
                    @click="closeLabelDropdown"
                  />
                </div>
                <div class="flex flex-col max-h-60 min-h-0">
                  <header class="py-2 px-2.5">
                    <Input
                      v-model="labelQuery"
                      type="search"
                      :placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
                      icon-left="i-lucide-search"
                      size="sm"
                      class="w-full"
                    />
                  </header>
                  <ul
                    v-if="labels.length > 0"
                    class="flex-1 overflow-y-auto m-0 list-none"
                    role="listbox"
                  >
                    <li
                      v-for="label in filteredLabels"
                      :key="label.id"
                      class="my-1 mx-0 py-0 px-2.5"
                      role="option"
                    >
                      <button
                        class="w-full items-center rounded-md cursor-pointer flex py-1 px-2.5 hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
                        :class="{
                          'bg-n-slate-2 dark:bg-n-solid-2':
                            selectedLabel === label.title,
                        }"
                        @click="selectLabel(label.title)"
                      >
                        <span
                          class="rounded-md h-3 w-3 flex-shrink-0 border border-solid border-n-weak mr-2.5"
                          :style="{ backgroundColor: label.color }"
                        />
                        <span
                          class="overflow-hidden flex-grow w-full text-sm whitespace-nowrap text-ellipsis text-left"
                        >
                          {{ label.title }}
                        </span>
                      </button>
                    </li>
                  </ul>
                  <div v-else class="p-2 text-center">
                    <span class="text-sm text-n-slate-11">{{
                      t('CONTACTS_BULK_ACTIONS.NO_LABELS_FOUND')
                    }}</span>
                  </div>
                  <footer
                    v-if="selectedLabel"
                    class="p-2 border-t border-n-weak"
                  >
                    <Button
                      sm
                      type="button"
                      class="w-full"
                      :label="t('CONTACTS_LAYOUT.HEADER.CLEAR_LABEL_FILTER')"
                      @click="clearLabelFilter"
                    />
                  </footer>
                </div>
              </div>
            </div>
            <Button
              v-if="
                hasActiveFilters &&
                !isSegmentsView &&
                !isLabelView &&
                !isActiveView
              "
              icon="i-lucide-save"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('createSegment')"
            />
            <Button
              v-if="isSegmentsView && !isLabelView && !isActiveView"
              icon="i-lucide-trash"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('deleteSegment')"
            />
            <ContactSortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            <ContactMoreActions
              @add="emit('add')"
              @import="emit('import')"
              @export="emit('export')"
              @download-template="emit('downloadTemplate')"
              @basic-import="emit('basicImport')"
              @basic-export="emit('basicExport')"
            />
          </div>
          <div class="w-px h-4 bg-n-strong" />
          <ComposeConversation>
            <template #trigger>
              <Button :label="buttonLabel" size="sm" />
            </template>
          </ComposeConversation>
        </div>
      </div>
    </div>
  </header>
</template>
